/**
 * 이상한 방 - 매치 세션의 꾸미기 배선 (도구 ↔ 전송 · 씬 · 소리 · 토스트)
 *
 * 근거 문서: `이상한방-컨셉시트-v0.1.md` 3절(꾸미기 1분) · 4.1절(인당 배치 수)
 *            `이상한방-상태모델-v0.1.md` 2.1절(`placedBy`는 서버만 안다) · 11.3절(검증은 서버가 쥔다)
 *            로드맵 M10
 *
 * `matchSessionChanger.ts`의 형제다 - 같은 문 넷을 같은 이유로 연다(도구가 보내는 이벤트 · 도구가 읽는 뷰의 조각 ·
 * 도구가 겨눈 미리보기 · 호스트의 답). 규칙은 여기에 하나도 없다.
 *
 * ## 방이 **한 조각씩** 자란다 - `roomsChanged`를 쓰지 않는 이유
 *
 * 꾸미기의 방은 한 페이즈 안에서 수십 번 바뀐다(8명 × 8개). 그때마다 방 전체를 다시 실어 보내면 1분 동안 7KB짜리
 * 메시지가 수백 통이다. 그래서 호스트는 바뀐 한 조각만 알린다(`placed` · `unplaced`) - 이 파일이 그것을 받아
 * `scene.decorate(...)`로 씬의 방에 더하고 뺀다. 페이즈가 끝날 때 하한을 채우고 나서야 `roomsChanged`가 한 번 온다
 * (`matchPhases.leaveDecorate`) - 그 한 통이 전원의 방을 같은 모습으로 다시 앉힌다.
 *
 * ## 남이 놓은 것도 내 화면에 나타난다 - 그래서 **소리는 내 것에만** 울린다
 *
 * `placed`는 전원에게 간다(방이 공유 상태이므로). 소리까지 전원에게 울리면 1분 내내 딸깍거린다 - `by === me`일 때만
 * 울린다. `matchSessionChanger`의 `changerProgress`가 같은 이유로 내 것만 도구에 넘기는 것과 같은 규약이다.
 */

import { decodeObject } from '../core/roomCodec'
import type { VerbRejection } from '../core/verbs'
import { t } from '../i18n'
import type { DecorateTool, DecorateToolEvent, DecorateViewSnapshot } from '../input/decorateTool'
import type { GameHudHandle } from '../ui/gameHud'
import type { MatchEffectBody, PlayerEvent } from './match/matchTypes'
import type { ClientView } from './match/matchView'
import { rejectionKey } from './matchSessionChanger'
import type { MatchScene } from './matchSessionScene'
import type { SessionAudio } from './sessionAudio'
import type { RoomState } from '../core/types'

/* --- 타입 ------------------------------------------------------------------- */

/** 호스트의 답 중 꾸미기에 관한 셋. */
export type DecorateEffect = Extract<MatchEffectBody, { type: 'placed' | 'unplaced' | 'placeRejected' }>

/** 세션이 도구에서 필요로 하는 표면. 지금은 뷰 갱신 하나뿐이다 - 방은 씬이 쥐고 도구는 조회만 한다. */
export type DecorateLink = Pick<DecorateTool, 'onView'>

/** 무대가 도구를 세울 때 필요한 것 전부. 세션이 만들어 내준다(`MatchSession.decorator`). */
export type MatchDecoratePort = Readonly<{
  snapshot: () => DecorateViewSnapshot
  /** 지금 방. 꾸미기에는 초안이 없으므로 언제나 원본 하나다. */
  room: () => RoomState | null
  send: (event: DecorateToolEvent) => number
  /** 도구가 미리 잠근 요청의 사유. 호스트의 `placeRejected`와 **같은 말**로 띄운다. */
  showRejection: (reason: VerbRejection) => void
}>

export type MatchDecorateParts = Readonly<{
  view: () => ClientView | null
  scene: MatchScene
  hud: GameHudHandle
  send: (event: PlayerEvent) => number
}>

/* --- 뷰 조각 ------------------------------------------------------------------ */

/**
 * `ClientView` → 도구가 읽는 조각. 이름 옮기기만이다 - "지금 꾸밀 수 있는가"도 남은 개수도 한도도 전부 호스트가 내린
 * 값이고, 여기서 `phase`를 다시 보거나 방을 다시 세지 않는다(`changerSnapshot`과 같은 규약).
 */
export function decorateSnapshot(view: ClientView | null): DecorateViewSnapshot {
  if (view === null) return { active: false, left: 0, limit: 0 }
  return { active: view.self.canDecorate, left: view.self.placementsLeft, limit: view.self.placementsLimit }
}

/* --- 문 ----------------------------------------------------------------------- */

export function createMatchDecoratePort(parts: MatchDecorateParts): MatchDecoratePort {
  return {
    snapshot: () => decorateSnapshot(parts.view()),
    // 꾸미기 방에는 변경본이 없다 - `mutated`를 보면 이전 라운드의 잔상을 그릴 수 있다.
    room: () => parts.scene.rooms().original,
    send: (event) => parts.send(event),
    // 구획은 꾸미기에 없다(전원이 방 전체를 함께 채운다) - `hasZone`은 언제나 거짓이다.
    showRejection: (reason) => parts.hud.showNotice(t(rejectionKey(reason, false)), 'warn'),
  }
}

/* --- 호스트의 답 ---------------------------------------------------------------- */

/**
 * 꾸미기 효과 하나 → 씬·도구·소리·토스트.
 *
 * **씬은 도구가 서 있든 아니든 갱신한다.** `placed`는 전원에게 가고 그중에는 도구를 못 세운 사람(늦게 들어온 관전자)도
 * 있는데, 그 사람의 방만 낡은 채로 두면 페이즈가 끝난 뒤의 `roomsChanged` 전까지 화면이 거짓말을 한다.
 * 반대로 소리·토스트·띠는 내 것일 때만 - 남의 손이 내 화면에서 울릴 이유가 없다.
 */
export function routeDecorateEffect(
  effect: DecorateEffect,
  link: DecorateLink | null,
  parts: Readonly<{ decorate: MatchScene['decorate']; audio: SessionAudio; hud: GameHudHandle; me: string | null }>,
): void {
  switch (effect.type) {
    case 'placed':
      // 와이어에는 `placedBy`가 없다(2.1절). 씬은 그 칸을 쓰지 않으므로 기본 배치자로 복원한다 -
      // "내가 놓은 것인가"는 id의 접두사로 알고(`input/decorateTool.mineAt`) 진짜 판정은 호스트가 쥔다.
      parts.decorate({ placed: decodeObject(effect.obj) })
      if (effect.by === parts.me) parts.audio.play('changeAccepted')
      link?.onView()
      return
    case 'unplaced':
      parts.decorate({ unplaced: effect.id })
      link?.onView()
      return
    case 'placeRejected':
      // 거부는 애초에 나에게만 온다(`matchDecorate.onPlace`) - 그래도 도구가 없으면 띄우지 않는다.
      if (link === null) return
      parts.hud.showNotice(t(rejectionKey(effect.reason, false)), 'warn')
      return
  }
}
